import axios from "axios";
import { API_BASE_URL, getAuthToken } from "../artistStudio/shared";

// API helpers for the Fan Home Page. Each section has its own call so one failing section never blocks the others.
const url = (path) => `${API_BASE_URL}/v1/fan-home/${path}`;
const config = (extra = {}) => {
  const token = getAuthToken();
  return { headers: { ...(token ? { token } : {}), "Content-Type": "application/json" }, timeout: 15000, ...extra };
};
const result = (response) => response.data?.result;

export const isSignedIn = () => Boolean(getAuthToken());

export const fanHomeApi = {
  continuePlaying: () => axios.get(url("continue"), config()).then(result),
  newReleases: () => axios.get(url("new-releases"), config()).then(result),
  recommended: () => axios.get(url("recommended"), config()).then(result),
  trending: () => axios.get(url("trending"), config()).then(result),
  banner: (params = {}) => axios.get(url("ads/banner"), config({ params })).then(result),
  promoted: (params = {}) => axios.get(url("ads/promoted"), config({ params })).then(result),
  adImpression: (body) => axios.post(url("ads/impression"), body, config()).then(result),
  adClick: (body) => axios.post(url("ads/click"), body, config()).then(result),
  playback: (body) => axios.post(url("playback"), body, config()).then(result),
  following: () => axios.get(url("following"), config()).then(result),
  follow: (artistSlug) => axios.post(url("follow"), { artist_slug: artistSlug }, config()).then(result),
  unfollow: (artistSlug) => axios.post(url("unfollow"), { artist_slug: artistSlug }, config()).then(result),
};

// Same call as playback, but it still goes through while the page is closing.
export const sendPlaybackOnExit = (body) => {
  const token = getAuthToken();
  if (!token || typeof fetch !== "function") return;
  try {
    fetch(url("playback"), { method: "POST", keepalive: true, headers: { token, "Content-Type": "application/json" }, body: JSON.stringify(body) });
  } catch (e) { /* nothing to do: the position is saved again on the next play */ }
};

export const apiStatus = (error) => error?.response?.status || null;
export const apiMessage = (error, fallback = "Something went wrong. Please try again.") => error?.response?.data?.message || fallback;

// ---- display helpers ----------------------------------------------------------------------------------------------
export const greetingFor = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

// The signed-in person's first name, from what login stored in the browser. Empty for guests.
export const storedFirstName = () => {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    const name = user?.first_name || user?.firstName || user?.name || user?.username || "";
    return String(name).trim().split(/\s+/)[0] || "";
  } catch (e) {
    return "";
  }
};

// The signed-in person's id, from what login stored in the browser (null when it isn't there).
export const storedUserId = () => {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    const user = raw ? JSON.parse(raw) : null;
    const id = user?.id ?? user?.user_id ?? user?._id ?? null;
    return id === null || id === undefined || id === "" ? null : String(id);
  } catch (e) {
    return null;
  }
};

export const formatPlays = (count) => {
  const n = Number(count);
  if (count === null || count === undefined || !Number.isFinite(n)) return "";
  const short = (value, suffix) => `${value >= 100 ? Math.round(value) : Math.round(value * 10) / 10}${suffix}`;
  if (n >= 1e9) return `${short(n / 1e9, "B")} plays`;
  if (n >= 1e6) return `${short(n / 1e6, "M")} plays`;
  if (n >= 1e3) return `${short(n / 1e3, "K")} plays`;
  return `${n} ${n === 1 ? "play" : "plays"}`;
};

export const timeAgo = (iso, now = Date.now()) => {
  const then = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(then)) return "";
  const days = Math.floor((now - then) / 86400000);
  if (days < 1) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (days < 30) return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (days < 365) return months === 1 ? "1 month ago" : `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
};

export const formatClock = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
